/**
 * Report job list — the page body shared by Transaction Reports and Settlement
 * Reports. Both are pre-generated daily XLSX files per merchant produced by the
 * same worker pipeline; `kind` only selects the API routes and the copy.
 *
 * Cron at 04:00 GMT+7 generates v1 for the previous day. After
 * SETTING_REPORTS_RECHECK_AFTER_DAYS (default 4) days, the worker rechecks
 * for drift; if the content hash differs, a v2 (+v3, ...) row appears.
 *
 *   transaction — period = the day the transactions were CREATED
 *   settlement  — period = the day the deposits SETTLED (Pending → Active; the
 *                 Wallet's settlement mutations)
 *
 * Permissions (same for both kinds):
 *   `report.transaction.view`       — see the list + open detail
 *   `report.transaction.download`   — actually download a file
 *   `report.transaction.view.all`   — internal-only: omit merchant filter to
 *                                     see every merchant's reports
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBan,
  IconCheck,
  IconClock,
  IconDownload,
  IconHourglass,
  IconRefresh,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  reportsApiByKind,
  type ReportJobRow,
  type ReportJobStatus,
  type ReportKind,
} from '../api/reports';
import { merchantsApi } from '../api/merchants';
import { ApiCallError } from '../api/client';
import { useSession, usePermissions } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { formatGmt7, formatIdr } from '../lib/format';
import { notifyError } from '../lib/notify';
import { useTranslation } from '../i18n/LocaleContext';
import { DataTable } from './DataTable';
import type { TranslationKey } from '../i18n/translations';

// Copy that differs per report kind. Everything else reuses `reports.*`.
type KindCopy = Record<
  | 'title'
  | 'subtitleMerchant'
  | 'subtitleInternal'
  | 'scheduleInfo'
  | 'colPeriod'
  | 'colRows'
  | 'empty'
  | 'noTransactions'
  | 'noTransactionsDetail'
  | 'detailTitle',
  TranslationKey
>;
const KIND_COPY: Record<ReportKind, KindCopy> = {
  transaction: {
    title: 'reports.title',
    subtitleMerchant: 'reports.subtitleMerchant',
    subtitleInternal: 'reports.subtitleInternal',
    scheduleInfo: 'reports.scheduleInfo',
    colPeriod: 'reports.colPeriod',
    colRows: 'reports.colRows',
    empty: 'reports.empty',
    noTransactions: 'reports.noTransactions',
    noTransactionsDetail: 'reports.noTransactionsDetail',
    detailTitle: 'reports.detailTitle',
  },
  settlement: {
    title: 'settlement.title',
    subtitleMerchant: 'settlement.subtitleMerchant',
    subtitleInternal: 'settlement.subtitleInternal',
    scheduleInfo: 'settlement.scheduleInfo',
    colPeriod: 'settlement.colPeriod',
    colRows: 'settlement.colRows',
    empty: 'settlement.empty',
    noTransactions: 'settlement.noTransactions',
    noTransactionsDetail: 'settlement.noTransactionsDetail',
    detailTitle: 'settlement.detailTitle',
  },
};

export function ReportJobsView({ kind }: { kind: ReportKind }) {
  const { t } = useTranslation();
  const copy = KIND_COPY[kind];
  const api = reportsApiByKind[kind];
  const isSettlement = kind === 'settlement';
  const { state } = useSession();
  const { has, isSuperuser } = usePermissions();
  const isPlatformUser =
    state.status === 'authenticated' && isPlatform(state.profile);
  const canDownload = isSuperuser || has('report.transaction.download');
  const canViewAll = isSuperuser || has('report.transaction.view.all');

  // Filter draft (no refetch on type — only on Apply).
  const [draft, setDraft] = useState<{
    fromDate: string;
    toDate: string;
    merchantId: string;
    status: ReportJobStatus | '';
  }>({ fromDate: '', toDate: '', merchantId: '', status: '' });
  const [applied, setApplied] = useState<typeof draft>(draft);
  const [detailOpenFor, setDetailOpenFor] = useState<ReportJobRow | null>(null);

  // Merchants list for the (internal-only) filter dropdown. The list endpoint
  // caps `limit` at 100 (higher values 400), so request exactly the max.
  const merchantsQuery = useQuery({
    queryKey: ['merchants-for-report-filter'],
    queryFn: () => merchantsApi.list({ limit: 100 }),
    enabled: isPlatformUser && canViewAll,
  });

  const reportsQuery = useQuery({
    queryKey: ['reports.list', kind, applied],
    queryFn: () =>
      api.list({
        fromDate: applied.fromDate || undefined,
        toDate: applied.toDate || undefined,
        merchantId: applied.merchantId || undefined,
        limit: 50,
      }),
  });

  const filtered = useMemo(() => {
    if (!reportsQuery.data) return [];
    if (!applied.status) return reportsQuery.data.rows;
    return reportsQuery.data.rows.filter((r) => r.status === applied.status);
  }, [reportsQuery.data, applied.status]);

  const applyFilter = () => setApplied(draft);
  const resetFilter = () => {
    const e = { fromDate: '', toDate: '', merchantId: '', status: '' as const };
    setDraft(e);
    setApplied(e);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t(copy.title)}</Title>
          <Text c="dimmed" size="sm">
            {isPlatformUser ? t(copy.subtitleInternal) : t(copy.subtitleMerchant)}
          </Text>
        </div>
      </Group>

      <Alert color="blue" icon={<IconAlertCircle size={16} />} variant="light">
        {t(copy.scheduleInfo)}
      </Alert>

      <Card withBorder>
        <Stack gap="sm">
          <Group grow align="end">
            <TextInput
              type="date"
              label={t('reports.fromDate')}
              value={draft.fromDate}
              onChange={(e) =>
                setDraft({ ...draft, fromDate: e.currentTarget.value })
              }
            />
            <TextInput
              type="date"
              label={t('reports.toDate')}
              value={draft.toDate}
              onChange={(e) =>
                setDraft({ ...draft, toDate: e.currentTarget.value })
              }
            />
            {isPlatformUser && canViewAll && (
              <Select
                label={t('reports.merchantFilter')}
                placeholder={t('reports.merchantAll')}
                clearable
                searchable
                data={[
                  ...(merchantsQuery.data?.rows ?? []).map((m) => ({
                    value: m.id,
                    label: m.name,
                  })),
                ]}
                value={draft.merchantId || null}
                onChange={(v) => setDraft({ ...draft, merchantId: v ?? '' })}
              />
            )}
            <Select
              label={t('reports.statusFilter')}
              placeholder={t('reports.statusAll')}
              clearable
              data={[
                { value: 'ready', label: t('reports.statusReady') },
                { value: 'pending', label: t('reports.statusPending') },
                { value: 'generating', label: t('reports.statusGenerating') },
                { value: 'regenerating', label: t('reports.statusRegenerating') },
                { value: 'failed', label: t('reports.statusFailed') },
                { value: 'no_data', label: t('reports.statusNoData') },
              ]}
              value={draft.status || null}
              onChange={(v) =>
                setDraft({ ...draft, status: (v as ReportJobStatus) ?? '' })
              }
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={resetFilter}>
              {t('common.reset')}
            </Button>
            <Button size="xs" onClick={applyFilter}>
              {t('common.apply')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder p={0}>
        {reportsQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : reportsQuery.error ? (
          <Alert color="red" m="md" icon={<IconAlertCircle size={16} />}>
            {(reportsQuery.error as Error).message}
          </Alert>
        ) : (
          <DataTable
            tableId={`reports-${isSettlement ? 'settlement' : 'transaction'}${isPlatformUser ? '-all' : ''}`}
            columns={[
              { key: 'period', label: t(copy.colPeriod), width: 170 },
              ...(isPlatformUser ? [{ key: 'merchant', label: t('reports.colMerchant'), width: 220 }] : []),
              { key: 'status', label: t('reports.colStatus'), width: 130 },
              { key: 'version', label: t('reports.colVersion'), width: 100 },
              { key: 'rows', label: t(copy.colRows), width: 110 },
              ...(isSettlement ? [{ key: 'net', label: t('settlement.colNet'), width: 160 }] : []),
              { key: 'generated', label: t('reports.colGenerated'), width: 170 },
              { key: 'size', label: t('reports.colSize'), width: 100 },
              { key: 'action', width: 170, fixed: true, sticky: 'right' },
            ]}
          >
                {filtered.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={(isPlatformUser ? 8 : 7) + (isSettlement ? 1 : 0)}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t(copy.empty)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {filtered.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {r.periodDateYmd}
                      </Text>
                    </Table.Td>
                    {isPlatformUser && (
                      <Table.Td>
                        <Text size="sm">{r.merchantName ?? '—'}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {r.merchantId.slice(0, 8)}…
                        </Text>
                      </Table.Td>
                    )}
                    <Table.Td>
                      <StatusBadge status={r.status} t={t} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge variant="light">v{r.fileVersion}</Badge>
                        {r.fileVersion > 1 && (
                          <Tooltip
                            label={t('reports.regeneratedTooltip', {
                              reason:
                                r.regeneratedReason === 'recheck_drift'
                                  ? t('reports.reasonRecheckDrift')
                                  : r.regeneratedReason === 'manual_admin'
                                    ? t('reports.reasonManualAdmin')
                                    : (r.regeneratedReason ?? '—'),
                              date: formatGmt7(r.generatedAt),
                            })}
                            withArrow
                          >
                            <Badge size="xs" color="yellow" variant="filled">
                              {t('reports.updatedBadge')}
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {r.rowCount}
                        {!isSettlement && (
                          <>
                            {' '}
                            <Text component="span" c="dimmed" size="xs">
                              ({r.cashinCount} in / {r.cashoutCount} out)
                            </Text>
                          </>
                        )}
                      </Text>
                      {r.pendingCount > 0 && (
                        <Text size="xs" c="orange">
                          {r.pendingCount} {t('reports.pendingRowsHint')}
                        </Text>
                      )}
                    </Table.Td>
                    {isSettlement && (
                      <Table.Td>
                        <Text size="sm">{r.totalNet !== null ? formatIdr(r.totalNet) : '—'}</Text>
                        {r.totalGross !== null && r.totalFee !== null && r.rowCount > 0 && (
                          <Text size="xs" c="dimmed">
                            {t('settlement.grossFeeHint', {
                              gross: formatIdr(r.totalGross),
                              fee: formatIdr(r.totalFee),
                            })}
                          </Text>
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatGmt7(r.generatedAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {r.fileSizeBytes
                          ? `${(r.fileSizeBytes / 1024).toFixed(1)} KB`
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {r.status === 'no_data' ? (
                          <Text size="xs" c="dimmed">{t(copy.noTransactions)}</Text>
                        ) : (
                          <DownloadButton
                            kind={kind}
                            row={r}
                            enabled={
                              canDownload && r.status === 'ready' && r.fileSizeBytes !== null
                            }
                            t={t}
                          />
                        )}
                        <ActionIcon
                          variant="subtle"
                          onClick={() => setDetailOpenFor(r)}
                          aria-label={t('reports.openDetail')}
                        >
                          <IconAlertCircle size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
          </DataTable>
        )}
      </Card>

      {detailOpenFor && (
        <ReportDetailModal
          kind={kind}
          row={detailOpenFor}
          onClose={() => setDetailOpenFor(null)}
          canDownload={canDownload}
        />
      )}
    </Stack>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: ReportJobStatus;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (status === 'ready')
    return (
      <Badge color="green" leftSection={<IconCheck size={10} />}>
        {t('reports.statusReady')}
      </Badge>
    );
  if (status === 'pending')
    return (
      <Badge color="gray" leftSection={<IconHourglass size={10} />}>
        {t('reports.statusPending')}
      </Badge>
    );
  if (status === 'generating')
    return (
      <Badge color="blue" leftSection={<IconClock size={10} />}>
        {t('reports.statusGenerating')}
      </Badge>
    );
  if (status === 'regenerating')
    return (
      <Badge color="yellow" leftSection={<IconRefresh size={10} />}>
        {t('reports.statusRegenerating')}
      </Badge>
    );
  if (status === 'no_data')
    return (
      <Badge color="gray" variant="light" leftSection={<IconAlertCircle size={10} />}>
        {t('reports.statusNoData')}
      </Badge>
    );
  return (
    <Badge color="red" leftSection={<IconAlertTriangle size={10} />}>
      {t('reports.statusFailed')}
    </Badge>
  );
}

function DownloadButton({
  kind,
  row,
  enabled,
  t,
}: {
  kind: ReportKind;
  row: ReportJobRow;
  enabled: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const mut = useMutation({
    meta: { successNotification: false, errorNotification: false },
    mutationFn: () => reportsApiByKind[kind].download(row.id),
    onSuccess: (data) => {
      // Open in a new tab so the page stays interactive while the file
      // downloads. Browser will use the Content-Disposition filename.
      window.open(data.url, '_blank', 'noopener,noreferrer');
    },
    onError: (e) => {
      if (e instanceof ApiCallError) notifyError(`${e.code}: ${e.message}`);
      else notifyError((e as Error).message);
    },
  });
  return (
    <Tooltip
      label={enabled ? t('reports.downloadTooltip') : t('reports.downloadDisabledTooltip')}
      withArrow
    >
      <ActionIcon
        variant="filled"
        color="green"
        disabled={!enabled}
        loading={mut.isPending}
        onClick={() => mut.mutate()}
        aria-label={t('reports.download')}
      >
        <IconDownload size={16} />
      </ActionIcon>
    </Tooltip>
  );
}

function ReportDetailModal({
  kind,
  row,
  onClose,
  canDownload,
}: {
  kind: ReportKind;
  row: ReportJobRow;
  onClose: () => void;
  canDownload: boolean;
}) {
  const { t } = useTranslation();
  const copy = KIND_COPY[kind];
  const api = reportsApiByKind[kind];
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['reports.detail', kind, row.id],
    queryFn: () => api.get(row.id),
  });
  const regen = useMutation({
    meta: {
      successKey: 'notify.report.regen.ok',
      errorKey: 'notify.report.regen.fail',
    },
    mutationFn: () =>
      api.regenerate({
        merchantId: row.merchantId,
        periodDateYmd: row.periodDateYmd,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports.list'] });
      onClose();
    },
  });
  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={t(copy.detailTitle, { period: row.periodDateYmd })}
    >
      <Stack>
        <Group grow>
          <FieldRow label={t('reports.fieldMerchant')} value={row.merchantName ?? '—'} />
          <FieldRow label={t('reports.fieldStatus')} value={row.status} />
        </Group>
        <Group grow>
          <FieldRow label={t('reports.fieldVersion')} value={`v${row.fileVersion}`} />
          <FieldRow
            label={t('reports.fieldGeneratedBy')}
            value={row.generatedBy}
          />
        </Group>
        <Group grow>
          <FieldRow
            label={t('reports.fieldGeneratedAt')}
            value={formatGmt7(row.generatedAt)}
          />
          <FieldRow
            label={t('reports.fieldLastChecked')}
            value={row.lastCheckedAt ? formatGmt7(row.lastCheckedAt) : '—'}
          />
        </Group>
        <Group grow>
          <FieldRow label={t('reports.fieldRows')} value={String(row.rowCount)} />
          <FieldRow
            label={t('reports.fieldHash')}
            value={row.dataHash ? row.dataHash.slice(0, 12) + '…' : '—'}
          />
        </Group>
        {kind === 'settlement' && row.totalNet !== null && (
          <Group grow>
            <FieldRow label={t('settlement.fieldGross')} value={formatIdr(row.totalGross ?? 0)} />
            <FieldRow label={t('settlement.fieldFee')} value={formatIdr(row.totalFee ?? 0)} />
            <FieldRow label={t('settlement.fieldNet')} value={formatIdr(row.totalNet)} />
          </Group>
        )}
        {row.regeneratedReason && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            {t('reports.regeneratedAlert', {
              reason:
                row.regeneratedReason === 'recheck_drift'
                  ? t('reports.reasonRecheckDrift')
                  : row.regeneratedReason === 'manual_admin'
                    ? t('reports.reasonManualAdmin')
                    : row.regeneratedReason,
            })}
          </Alert>
        )}
        {row.failureReason && (
          <Alert color="red" icon={<IconBan size={16} />}>
            {row.failureReason}
          </Alert>
        )}
        {row.status === 'no_data' && (
          <Alert color="gray" icon={<IconAlertCircle size={16} />} variant="light">
            {t(copy.noTransactionsDetail)}
          </Alert>
        )}
        {detail.data && detail.data.versionChain.length > 1 && (
          <div>
            <Text fw={600} size="sm" mb={4}>
              {t('reports.versionHistory')}
            </Text>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>v</Table.Th>
                  <Table.Th>{t('reports.colStatus')}</Table.Th>
                  <Table.Th>{t('reports.colGenerated')}</Table.Th>
                  <Table.Th>{t('reports.colReason')}</Table.Th>
                  <Table.Th>{t('reports.colRows')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detail.data.versionChain.map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>
                      <Code>v{v.fileVersion}</Code>
                    </Table.Td>
                    <Table.Td>{v.status}</Table.Td>
                    <Table.Td>{formatGmt7(v.generatedAt)}</Table.Td>
                    <Table.Td>{v.regeneratedReason ?? '—'}</Table.Td>
                    <Table.Td>{v.rowCount}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
        <Group justify="flex-end">
          {canDownload && (
            <Button
              variant="default"
              loading={regen.isPending}
              onClick={() => regen.mutate()}
              leftSection={<IconRefresh size={14} />}
            >
              {t('reports.regenerateBtn')}
            </Button>
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {value}
      </Text>
    </div>
  );
}
